import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import {
  Subject,
  finalize,
  takeUntil,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { ToastService } from '@black/ui';
import {
  BatchService,
  BatchItem,
  BatchStatus,
  BulkEnrollResult,
} from '../../shared/services/batch.service';
import { CourseService } from '../../shared/core/state/course/course.service';
import { LiveClassService } from '../../shared/core/state/live-class/live-class.service';
import { UserService } from '../../shared/core/state/users/users.service';

@Component({
  selector: 'black-batch-management',
  templateUrl: './batch-management.component.html',
  styles: [],
  standalone: false,
})
export class BatchManagementComponent implements OnInit, OnDestroy {
  private batchService = inject(BatchService);
  private courseService = inject(CourseService);
  private liveClassService = inject(LiveClassService);
  private userService = inject(UserService);
  private toast = inject(ToastService);

  readonly BatchStatus = BatchStatus;
  private destroy$ = new Subject<void>();

  // List state
  batches: BatchItem[] = [];
  totalBatches = 0;
  page = 1;
  limit = 10;
  search = '';
  statusFilter = '';
  loading = false;

  // Selected Batch Drawer
  selectedBatch: BatchItem | null = null;
  activeDrawerTab: 'CURRICULUM' | 'STUDENTS' = 'CURRICULUM';

  // Create / Edit Batch Modal
  showBatchModal = false;
  editingBatchId: string | null = null;
  batchForm = {
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    status: BatchStatus.ACTIVE,
  };
  submittingBatch = signal(false);

  // Add Curriculum Modals
  showAddCourseModal = false;
  availableCourses: any[] = [];
  selectedCourseIdToAdd = '';
  loadingCourses = false;

  showAddLiveClassModal = false;
  availableLiveClasses: any[] = [];
  selectedLiveClassIdToAdd = '';
  loadingLiveClasses = false;

  // Add Students Modal
  showAddStudentModal = false;
  tenantStudents: any[] = [];
  studentSearch = '';
  selectedStudentIdsToAdd = new Set<string>();
  loadingStudents = false;
  studentSearchSubject$ = new Subject<string>();
  studentPage = 1;
  studentTotalPages = 1;
  studentTotal = 0;
  readonly studentPageSize = 20;

  // Student Roster (in-batch) search + pagination
  rosterSearch = '';
  rosterPage = 1;
  readonly rosterPageSize = 20;

  // Bulk Enroll Modal
  showBulkEnrollModal = false;
  bulkTargetType: 'COURSE' | 'LIVE_CLASS' = 'COURSE';
  bulkTargetId = '';
  bulkTargetTitle = '';
  bulkExpiresInDays = 365;
  submittingBulkEnroll = signal(false);

  ngOnInit(): void {
    this.loadBatches();

    // Debounce student search in the Add Students modal
    this.studentSearchSubject$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.studentPage = 1;
        this.loadTenantStudents();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadBatches(): void {
    this.loading = true;
    this.batchService
      .getBatches(this.page, this.limit, this.search, this.statusFilter)
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res: any) => {
          if (Array.isArray(res)) {
            this.batches = res;
            this.totalBatches = res.length;
          } else {
            this.batches = res?.docs ?? [];
            this.totalBatches = res?.totalDocs ?? 0;
          }
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to load batches.',
            'error',
          );
        },
      });
  }

  onSearchChange(): void {
    this.page = 1;
    this.loadBatches();
  }

  onStatusFilterChange(): void {
    this.page = 1;
    this.loadBatches();
  }

  // Detail View (Full Page)
  selectBatch(batch: BatchItem): void {
    this.selectedBatch = batch;
    this.activeDrawerTab = 'CURRICULUM';
    this.refreshSelectedBatch();
  }

  deselectBatch(): void {
    this.selectedBatch = null;
    this.loadBatches();
  }

  refreshSelectedBatch(): void {
    if (!this.selectedBatch?._id) return;
    this.batchService
      .getBatchById(this.selectedBatch._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (batch) => {
          this.selectedBatch = batch;
          // Also update batch in list
          const idx = this.batches.findIndex((b) => b._id === batch._id);
          if (idx !== -1) {
            this.batches[idx] = batch;
          }
        },
      });
  }

  // Create / Edit Batch
  openCreateBatchModal(): void {
    this.editingBatchId = null;
    this.batchForm = {
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      status: BatchStatus.ACTIVE,
    };
    this.showBatchModal = true;
  }

  openEditBatchModal(batch: BatchItem, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.editingBatchId = batch._id;
    this.batchForm = {
      name: batch.name,
      description: batch.description || '',
      startDate: batch.startDate
        ? new Date(batch.startDate).toISOString().slice(0, 10)
        : '',
      endDate: batch.endDate
        ? new Date(batch.endDate).toISOString().slice(0, 10)
        : '',
      status: batch.status,
    };
    this.showBatchModal = true;
  }

  closeBatchModal(): void {
    this.showBatchModal = false;
    this.editingBatchId = null;
  }

  submitBatch(): void {
    if (!this.batchForm.name.trim()) {
      this.toast.showToast('Batch name is required.', 'error');
      return;
    }

    this.submittingBatch.set(true);

    const payload: Partial<BatchItem> = {
      name: this.batchForm.name.trim(),
      description: this.batchForm.description.trim(),
      startDate: this.batchForm.startDate || undefined,
      endDate: this.batchForm.endDate || undefined,
      status: this.batchForm.status,
    };

    const action$ = this.editingBatchId
      ? this.batchService.updateBatch(this.editingBatchId, payload)
      : this.batchService.createBatch(payload);

    action$
      .pipe(
        finalize(() => this.submittingBatch.set(false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (batch) => {
          this.toast.showToast(
            this.editingBatchId
              ? 'Batch updated successfully!'
              : 'Batch created successfully!',
            'success',
          );
          this.closeBatchModal();
          this.loadBatches();
          if (this.selectedBatch?._id === batch._id) {
            this.selectedBatch = batch;
          }
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to save batch.',
            'error',
          );
        },
      });
  }

  deleteBatch(batch: BatchItem, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    if (
      !confirm(`Are you sure you want to delete the batch "${batch.name}"?`)
    ) {
      return;
    }

    this.batchService
      .deleteBatch(batch._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.showToast('Batch deleted successfully.', 'success');
          if (this.selectedBatch?._id === batch._id) {
            this.deselectBatch();
          }
          this.loadBatches();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to delete batch.',
            'error',
          );
        },
      });
  }

  // Curriculum Management - Courses
  openAddCourseModal(): void {
    this.selectedCourseIdToAdd = '';
    this.showAddCourseModal = true;
    this.loadingCourses = true;
    this.courseService
      .getAll(1, 100, undefined, true)
      .pipe(
        finalize(() => (this.loadingCourses = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res: any) => {
          const currentCourseIds = new Set(
            (this.selectedBatch?.courses || []).map((c: any) => c._id || c),
          );
          const allCourses = res?.docs ?? [];
          this.availableCourses = allCourses.filter(
            (c: any) => !currentCourseIds.has(c._id),
          );
          if (this.availableCourses.length > 0) {
            this.selectedCourseIdToAdd = this.availableCourses[0]._id;
          }
        },
        error: () => {
          this.availableCourses = [];
        },
      });
  }

  closeAddCourseModal(): void {
    this.showAddCourseModal = false;
  }

  confirmAddCourse(): void {
    if (!this.selectedBatch?._id || !this.selectedCourseIdToAdd) return;
    this.batchService
      .addCourses(this.selectedBatch._id, [this.selectedCourseIdToAdd])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.showToast('Course added to batch.', 'success');
          this.closeAddCourseModal();
          this.refreshSelectedBatch();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to add course.',
            'error',
          );
        },
      });
  }

  removeCourseFromBatch(course: any): void {
    if (!this.selectedBatch?._id || !course?._id) return;
    const title = course.title || 'course';
    if (!confirm(`Remove "${title}" from this batch?`)) return;

    this.batchService
      .removeCourse(this.selectedBatch._id, course._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.showToast(`"${title}" removed from batch.`, 'success');
          this.refreshSelectedBatch();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to remove course.',
            'error',
          );
        },
      });
  }

  // Curriculum Management - Live Classes
  openAddLiveClassModal(): void {
    this.selectedLiveClassIdToAdd = '';
    this.showAddLiveClassModal = true;
    this.loadingLiveClasses = true;
    this.liveClassService
      .getAll(1, 100, undefined, true)
      .pipe(
        finalize(() => (this.loadingLiveClasses = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res: any) => {
          const currentClassIds = new Set(
            (this.selectedBatch?.liveClasses || []).map(
              (lc: any) => lc._id || lc,
            ),
          );
          const allClasses = res?.docs ?? [];
          this.availableLiveClasses = allClasses.filter(
            (lc: any) => !currentClassIds.has(lc._id || lc.id),
          );
          if (this.availableLiveClasses.length > 0) {
            this.selectedLiveClassIdToAdd =
              this.availableLiveClasses[0]._id ||
              this.availableLiveClasses[0].id;
          }
        },
        error: () => {
          this.availableLiveClasses = [];
        },
      });
  }

  closeAddLiveClassModal(): void {
    this.showAddLiveClassModal = false;
  }

  confirmAddLiveClass(): void {
    if (!this.selectedBatch?._id || !this.selectedLiveClassIdToAdd) return;
    this.batchService
      .addLiveClasses(this.selectedBatch._id, [this.selectedLiveClassIdToAdd])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.showToast('Live class added to batch.', 'success');
          this.closeAddLiveClassModal();
          this.refreshSelectedBatch();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to add live class.',
            'error',
          );
        },
      });
  }

  removeLiveClassFromBatch(liveClass: any): void {
    if (!this.selectedBatch?._id || !liveClass?._id) return;
    const title = liveClass.title || 'live class';
    if (!confirm(`Remove "${title}" from this batch?`)) return;

    this.batchService
      .removeLiveClass(this.selectedBatch._id, liveClass._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.showToast(`"${title}" removed from batch.`, 'success');
          this.refreshSelectedBatch();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to remove live class.',
            'error',
          );
        },
      });
  }

  // Student Management in Batch
  openAddStudentModal(): void {
    this.studentSearch = '';
    this.selectedStudentIdsToAdd.clear();
    this.studentPage = 1;
    this.showAddStudentModal = true;
    this.loadTenantStudents();
  }

  closeAddStudentModal(): void {
    this.showAddStudentModal = false;
    this.selectedStudentIdsToAdd.clear();
  }

  onStudentSearchChange(): void {
    this.studentSearchSubject$.next(this.studentSearch);
  }

  loadTenantStudents(): void {
    this.loadingStudents = true;
    this.userService
      .getAll(this.studentPage, this.studentPageSize, {
        role: 'STUDENT',
        search: this.studentSearch,
      })
      .pipe(
        finalize(() => (this.loadingStudents = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res: any) => {
          const currentStudentIds = new Set(
            (this.selectedBatch?.students || []).map((s: any) => s._id || s),
          );
          const allStudents = res?.docs ?? [];
          this.tenantStudents = allStudents.filter(
            (s: any) => !currentStudentIds.has(s._id),
          );
          this.studentTotal = res?.totalDocs ?? allStudents.length;
          this.studentTotalPages = res?.totalPages ?? 1;
        },
        error: () => {
          this.tenantStudents = [];
        },
      });
  }

  studentNextPage(): void {
    if (this.studentPage < this.studentTotalPages) {
      this.studentPage++;
      this.loadTenantStudents();
    }
  }

  studentPrevPage(): void {
    if (this.studentPage > 1) {
      this.studentPage--;
      this.loadTenantStudents();
    }
  }

  toggleStudentSelection(studentId: string): void {
    if (this.selectedStudentIdsToAdd.has(studentId)) {
      this.selectedStudentIdsToAdd.delete(studentId);
    } else {
      this.selectedStudentIdsToAdd.add(studentId);
    }
  }

  toggleSelectAllStudents(): void {
    const allVisible = this.tenantStudents.map((s) => s._id);
    const allSelected = allVisible.every((id) =>
      this.selectedStudentIdsToAdd.has(id),
    );
    if (allSelected) {
      allVisible.forEach((id) => this.selectedStudentIdsToAdd.delete(id));
    } else {
      allVisible.forEach((id) => this.selectedStudentIdsToAdd.add(id));
    }
  }

  areAllVisibleSelected(): boolean {
    return (
      this.tenantStudents.length > 0 &&
      this.tenantStudents.every((s) => this.selectedStudentIdsToAdd.has(s._id))
    );
  }

  confirmAddStudents(): void {
    if (!this.selectedBatch?._id || !this.selectedStudentIdsToAdd.size) return;
    const studentIds = Array.from(this.selectedStudentIdsToAdd);

    this.batchService
      .addStudents(this.selectedBatch._id, studentIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.showToast(
            `${studentIds.length} student(s) added to batch.`,
            'success',
          );
          this.closeAddStudentModal();
          this.refreshSelectedBatch();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to add students.',
            'error',
          );
        },
      });
  }

  removeStudentFromBatch(student: any): void {
    if (!this.selectedBatch?._id || !student?._id) return;
    const name = this.getStudentName(student);
    if (!confirm(`Remove ${name} from this batch?`)) return;

    this.batchService
      .removeStudent(this.selectedBatch._id, student._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.showToast(`${name} removed from batch.`, 'success');
          this.refreshSelectedBatch();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to remove student.',
            'error',
          );
        },
      });
  }

  // Roster (in-batch students) search + pagination
  get filteredRosterStudents(): any[] {
    const students = this.selectedBatch?.students ?? [];
    if (!this.rosterSearch.trim()) return students;
    const q = this.rosterSearch.toLowerCase();
    return students.filter((s: any) => {
      const name = this.getStudentName(s).toLowerCase();
      const email = (s?.email || s?.username || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }

  get pagedRosterStudents(): any[] {
    const start = (this.rosterPage - 1) * this.rosterPageSize;
    return this.filteredRosterStudents.slice(
      start,
      start + this.rosterPageSize,
    );
  }

  get rosterTotalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.filteredRosterStudents.length / this.rosterPageSize),
    );
  }

  onRosterSearchChange(): void {
    this.rosterPage = 1;
  }

  rosterNextPage(): void {
    if (this.rosterPage < this.rosterTotalPages) this.rosterPage++;
  }

  rosterPrevPage(): void {
    if (this.rosterPage > 1) this.rosterPage--;
  }

  // Bulk Enrollment
  openBulkEnrollModal(targetType: 'COURSE' | 'LIVE_CLASS', item: any): void {
    this.bulkTargetType = targetType;
    this.bulkTargetId = item._id || item.id;
    this.bulkTargetTitle =
      item.title || (targetType === 'COURSE' ? 'Course' : 'Live Class');
    this.bulkExpiresInDays = 365;
    this.showBulkEnrollModal = true;
  }

  closeBulkEnrollModal(): void {
    this.showBulkEnrollModal = false;
  }

  confirmBulkEnroll(): void {
    if (!this.selectedBatch?._id || !this.bulkTargetId) return;

    this.submittingBulkEnroll.set(true);
    this.batchService
      .bulkEnroll(this.selectedBatch._id, {
        targetType: this.bulkTargetType,
        targetId: this.bulkTargetId,
        expiresInDays: this.bulkExpiresInDays,
      })
      .pipe(
        finalize(() => this.submittingBulkEnroll.set(false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res: BulkEnrollResult) => {
          this.toast.showToast(res.message, 'success');
          this.closeBulkEnrollModal();
        },
        error: (err) => {
          this.toast.showToast(
            err?.error?.message ?? 'Failed to bulk enroll students.',
            'error',
          );
        },
      });
  }

  // Helpers
  getStudentName(student: any): string {
    const p = student?.profile?.personal;
    if (p?.firstName) {
      return `${p.firstName} ${p.lastName ?? ''}`.trim();
    }
    return student?.username || 'Unknown Student';
  }

  getStudentInitial(student: any): string {
    return this.getStudentName(student).charAt(0).toUpperCase() || 'S';
  }

  formatDate(date?: string | Date): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
}
